import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Container,
  Divider,
  Form,
  Grid,
  Segment,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import { required, stringLength } from '@mhub/web-validations/dist/validations';

import { flashErrorMessage, flashMessage } from '../../actions/flash';
import { fetchM, serverRequestGet } from '../../actions/fetchM';

import AppForm from '../../components/AppForm';
import NavBar from '../../components/NavBar';
import CurrencySettings from './CurrencySettings';
import { fetchCurrencySetting } from '../../actions/currencySetting';
import { isForeignCompany } from '../../utils/companies';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

class CompanyCurrencies extends Component {
  static propTypes = {
    auth: PropTypes.shape({
      company: PropTypes.object,
    }),
    currencySetting: PropTypes.shape({
      data: PropTypes.object,
    }),
  }
  static defaultCurrencySetting = {
    id: '',
    isAbbreviationEnabled: false,
    displayDecimal: true,
    abbreviationDecimalPoints: 2,
    decimalSeparator: '.',
    thousandsSeparator: ',',
    millionsSeparator: ',',
    billionsSeparator: ',',
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  static mapStateToProps = state => ({
    auth: state.auth,
    appConfig: state.appConfig,
    currencySetting: state.currencySetting,
  })
  static getPropsToState = (props) => {
    const currencySetting = props.currencySetting.data && props.currencySetting.data.id ? props.currencySetting.data : CompanyCurrencies.defaultCurrencySetting;
    const currencySettingToken = props.currencySetting.token;
    return {
      currencySetting,
      currencySettingToken,
    };
  }
  constructor(props) {
    super(props);
    const state = CompanyCurrencies.getPropsToState(props);
    this.state = {
      loading: true,
      data: [],
      token: null,
      preview: false,
      hasChanges: false,
      errMessages: [],
      ...state,
    };
    this.validation = {
      billionsSeparator: createRef(),
      millionsSeparator: createRef(),
      thousandsSeparator : createRef(),
      decimalSeparator: createRef(),
    };
  }
  componentDidMount() {
    this.fetchCompanyCurrencies(this.props.auth.company.id);
  }
  componentWillReceiveProps(nextProps) {
    const state = CompanyCurrencies.getPropsToState(nextProps);
    this.setState({
      ...state,
    });
  }
  setCompanyCurrencies(data = []) {
    return new Promise((res) => {
      this.setState({
        data: data || [],
      }, () => {
        res(data);
      });
    });
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async fetchCompanyCurrencies(companyId) {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    try {
      const response = await serverRequestGet(
        `/company/${companyId}/currency/`,
        store,
      );
      await this.setLoading(false);
      if (response.error) {
        await store.dispatch(flashErrorMessage({ message: response.message }));
        return;
      }
      await this.setCompanyCurrencies(response.data);
      await this.setComponentState({
        token: response.token,
      });
    } catch (e) {
      Bugsnag.notifyException(e);
      await this.setLoading(false);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
    }
  }
  async submitCurrencySetting() {
    await this.setLoading(true);
    const { store } = this.context;
    const { auth, t } = this.props;
    const { currencySetting, currencySettingToken } = this.state;
    const validations = await AppForm.validate(this.validation, currencySetting);
    const errMessages = validations.filter(v => !v.isValid);
    await this.setComponentState({
      errMessages,
    });
    if (errMessages.length > 0) {
      await this.setLoading(false);
      store.dispatch(flashMessage({
        header: t('Please fix form errors', { ns: TRANSLATION_FILES.translation }),
        list: errMessages.map(l => l.message),
        icon: 'warning sign',
        type: 'error',
        autoHideDuration: 5000,
      }));
      return;
    }
    let res;
    try {
      res = await store.dispatch(fetchM(`/company/${auth.company.id}/currency_setting/`, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': currencySettingToken,
        },
        body: JSON.stringify(currencySetting),
      }));
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      return;
    }
    const data = await res.json();
    this.setState({
      currencySetting: data,
      hasChanges: false,
      loading: false,
    });
    store.dispatch(fetchCurrencySetting());
    store.dispatch(flashMessage({
      header: t('Update success', { ns: TRANSLATION_FILES.translation }),
      message: `${t('Currency setting successfully updated', { ns: TRANSLATION_FILES.translation })}.`,
      icon: 'check',
      type: 'success',
      autoHideDuration: 5000,
    }));
  }
  handleInputChanges = (_e, { name, value }) => {
    const { currencySetting } = this.state;
    currencySetting[name] = value;
    this.setState({ currencySetting, hasChanges: true });
  }
  handleCheckBoxChanges = (_e, { name }) => {
    const { currencySetting } = this.state;
    currencySetting[name] = !currencySetting[name];
    this.setState({ currencySetting, hasChanges: true });
  }
  handleSubmit = (e) => {
    e.preventDefault();
    this.submitCurrencySetting();
  }
  render() {
    const { auth, t } = this.props;
    const { currencySetting, hasChanges } = this.state;
    return (
      <Container>
        <NavBar
          header={t('Currency Settings')}
          rightNav={isForeignCompany(auth.company) &&
            <Button
              color="green"
              content={t('Save', { ns: TRANSLATION_FILES.translation })}
              disabled={this.state.loading || !hasChanges}
              onClick={e => this.form.handleSubmit(e)}
            />
          }
        />
        {/* Deprecating */}
        <CurrencySettings
          data={this.state.data}
          loading={this.state.loading}
          token={this.state.token}
        />
        {/*  */}
        {
          isForeignCompany(auth.company) && (
            <div>
              <Divider style={{ marginTop: '4em' }} horizontal>{t('Currency Separator')}</Divider>
              <AppForm
                error={this.state.errMessages.length > 0}
                onSubmit={this.handleSubmit}
                ref={(ref) => { this.form = ref; }}
              >
                <Form.Group widths={4}>
                  <AppForm.Input
                    label={t('Billions Separator')}
                    name="billionsSeparator"
                    value={currencySetting.billionsSeparator}
                    validation={[required, {
                      validate: stringLength,
                      option: {
                        equal: 1,
                      },
                    }]}
                    ref={this.validation.billionsSeparator}
                    onChange={this.handleInputChanges}
                  />
                  <AppForm.Input
                    label={t('Millions Separator')}
                    name="millionsSeparator"
                    value={currencySetting.millionsSeparator}
                    validation={[required, {
                      validate: stringLength,
                      option: {
                        equal: 1,
                      },
                    }]}
                    ref={this.validation.millionsSeparator}
                    onChange={this.handleInputChanges}
                  />
                  <AppForm.Input
                    label={t('Thousands Separator')}
                    name="thousandsSeparator"
                    value={currencySetting.thousandsSeparator}
                    validation={[required, {
                      validate: stringLength,
                      option: {
                        equal: 1,
                      },
                    }]}
                    ref={this.validation.thousandsSeparator}
                    onChange={this.handleInputChanges}
                  />
                  <AppForm.Input
                    label={t('Decimal Separator')}
                    name="decimalSeparator"
                    value={currencySetting.decimalSeparator}
                    validation={[required, {
                      validate: stringLength,
                      option: {
                        equal: 1,
                      },
                    }]}
                    ref={this.validation.decimalSeparator}
                    onChange={this.handleInputChanges}
                  />
                </Form.Group>
              </AppForm>
              <Segment>
                <Form.Checkbox
                  label={t('Show cent value')}
                  checked={currencySetting.displayDecimal}
                  name="displayDecimal"
                  onChange={this.handleCheckBoxChanges}
                />
              </Segment>
              <Grid.Row>
                <Grid.Column style={{ textAlign: 'center' }}>
                  <Button
                    content={t('Preview', { ns: TRANSLATION_FILES.translation })}
                    disabled={this.state.loading || this.state.preview}
                    onClick={() => this.setState({ preview: true })}
                  />
                </Grid.Column>
              </Grid.Row>
            </div>
          )
        }
        {
          this.state.preview && (
            <Segment>
              <h4>{t('Preview', { ns: TRANSLATION_FILES.translation })}</h4>
              1 {currencySetting.billionsSeparator} 0 0 0 {currencySetting.millionsSeparator} 0 0 0 {currencySetting.thousandsSeparator} 0 0 0 {currencySetting.displayDecimal ? `${currencySetting.decimalSeparator} 0 0` : ''}
            </Segment>
          )
        }
      </Container>
    );
  }
}

export default withTranslation(namespaces)(connect(CompanyCurrencies.mapStateToProps)(CompanyCurrencies));



// WEBPACK FOOTER //
// ./src/containers/CompanyCurrencies/CompanyCurrencies.jsx